'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import posthog from 'posthog-js'

import { CONSENT_KEY, needsConsent, storedConsent } from '../lib/consent'

/* =========================================
   COOKIE CONSENT BANNER
   Shown to EEA/UK visitors only (resolved at the edge in proxy.ts, see
   app/lib/consent.ts). For them PostHog initializes opted out with
   in-memory persistence (see instrumentation-client.ts), so nothing is
   captured or stored until they accept here. The choice lives in
   localStorage; once a choice exists the banner never renders again.
   Visitors elsewhere never see the banner and are tracked from the
   first view.
   ========================================= */

export function CookieBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (needsConsent() && storedConsent() === null) setVisible(true)
  }, [])

  const decide = (consent: 'yes' | 'no') => {
    try {
      localStorage.setItem(CONSENT_KEY, consent)
    } catch {
      /* If the choice cannot be stored, still honor it this session. */
    }
    if (consent === 'yes') {
      posthog.set_config({ persistence: 'localStorage+cookie' })
      posthog.opt_in_capturing()
    } else {
      posthog.opt_out_capturing()
    }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
      <p className="cookie-banner-text">
        We use analytics cookies to understand how visitors use the site. See
        the{' '}
        <Link href="/privacy-policy" className="cookie-banner-link">
          Privacy Policy
        </Link>
        .
      </p>
      <div className="cookie-banner-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => decide('yes')}
        >
          Accept
        </button>
        <button
          type="button"
          className="cookie-banner-decline"
          onClick={() => decide('no')}
        >
          Decline
        </button>
      </div>
    </div>
  )
}
